import { useMemo } from 'react'
import Big from 'big.js'
import { useAtomValue } from 'jotai'

import {
  Totals,
  Grid,
  GridItem,
  InfoPopover,
  Box
} from '@sequencehq/core-components'
import { lineItemGroupsToBreakdownRows } from '@sequencehq/invoice-content'
import {
  invoiceAppliedCreditGrantTaxAtom,
  invoiceAppliedCreditGrantsAtom,
  invoiceAppliedCreditNotesAtom,
  invoiceBalanceAtom,
  invoiceCurrencyAtom,
  invoiceGrossTotalAtom,
  invoiceNetTotalAtom,
  invoicePaymentsAtom,
  lineItemGroupsAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { useIsInvoiceEditable } from 'Invoices/hooks/useIsInvoiceEditable'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useProjectedCreditGrantTotal } from 'Invoices/hooks/useCreditGrantTotals'
import { sumCurrentPayments } from 'Invoices/utils'
import { toInvoiceSettlement } from './invoiceSettlement'
import {
  EXPECTED_CREDIT_INFO_BODY,
  EXPECTED_CREDIT_LABEL,
  EXPECTED_CREDIT_TEST_ID
} from './lineItemTotals.constants'

export const LineItemTotals = () => {
  const currency = useAtomValue(invoiceCurrencyAtom) ?? 'USD'
  const netTotal = useAtomValue(invoiceNetTotalAtom) ?? 0
  const grossTotal = useAtomValue(invoiceGrossTotalAtom) ?? 0
  const lineItemGroups = useAtomValue(lineItemGroupsAtom) ?? []
  const appliedCreditNotes = useAtomValue(invoiceAppliedCreditNotesAtom)
  const appliedCreditGrants = useAtomValue(invoiceAppliedCreditGrantsAtom)
  const appliedCreditGrantTax = useAtomValue(invoiceAppliedCreditGrantTaxAtom)
  const payments = useAtomValue(invoicePaymentsAtom)
  const balance = useAtomValue(invoiceBalanceAtom)
  const isEditable = useIsInvoiceEditable()
  const { useCreditGrantLedger } = useFeatureFlags()

  const amountPaid = Number(sumCurrentPayments(payments))

  const breakdownRows = useMemo(
    () => lineItemGroupsToBreakdownRows({ lineItemGroups, currency }),
    [lineItemGroups, currency]
  )

  // V2 credit grants are gated on useCreditGrantLedger. A hidden category is
  // withheld from the settlement, not merely unrendered, so adjustment totals
  // only aggregate visible rows.
  const settlement = useMemo(
    () =>
      toInvoiceSettlement({
        appliedCreditNotes,
        appliedCreditGrants: useCreditGrantLedger ? appliedCreditGrants : [],
        appliedCreditGrantTax: useCreditGrantLedger
          ? appliedCreditGrantTax
          : undefined
      }),
    [
      appliedCreditNotes,
      appliedCreditGrants,
      appliedCreditGrantTax,
      useCreditGrantLedger
    ]
  )

  // Deductions from either category headline the balance instead of the gross
  // total, so this keys off the rows that survived the gating (and the applied
  // filter) rather than off the raw arrays.
  const hasCreditGrants = settlement.creditGrantRows.length > 0
  const hasCreditNotes = settlement.creditNoteRows.length > 0
  const showSettlement = hasCreditGrants || hasCreditNotes
  const hasPayment = amountPaid > 0

  const projectedCredit = useProjectedCreditGrantTotal()
  const showProjectedCredit = projectedCredit.gt(0)
  const showCreditDeductions = showSettlement || showProjectedCredit
  // Deductions and payments settle in one waterfall: gross Total, then the
  // credit deductions down to "Total due" (owed after credits), then the
  // payment down to "Amount remaining" (still to be paid). Each section only
  // renders when it applies; with none, the totals collapse to a plain gross
  // Total.
  const showDetailed = showCreditDeductions || hasPayment

  // "Total due" is what's owed after credits but before payments. With no
  // payment the ledger balance is that figure and is authoritative; with a
  // payment the balance also nets the payment, so use the credit-adjusted total
  // (the adjustment is negative). Same derivation is the fallback when the
  // balance is absent on older API responses.
  const creditAdjustedTotal = new Big(grossTotal)
    .plus(settlement.adjustmentGrossTotal)
    .minus(projectedCredit)
    .toString()
  const totalDue =
    hasPayment || showProjectedCredit
      ? creditAdjustedTotal
      : (balance ?? creditAdjustedTotal)

  // "Amount remaining" is what's still owed after payments — the ledger balance,
  // falling back to the credit-adjusted total less what has been paid.
  const amountRemaining =
    balance ?? new Big(creditAdjustedTotal).minus(amountPaid).toString()

  // "Total due" is an intermediate row when a payment follows, otherwise it is
  // the bold headline.
  const totalDueLine = hasPayment ? (
    <Totals.Row title="Total due" amount={totalDue} />
  ) : (
    <Totals.Total title="Total due" amount={totalDue} appendCurrencyCode />
  )

  const totalsBody = showDetailed ? (
    <>
      <Totals.Row title="Total" amount={grossTotal} />
      {showCreditDeductions && (
        <>
          <Totals.Divider />
          {showSettlement && (
            <>
              {settlement.creditGrantRows.map(row => (
                <Totals.Row
                  key={row.key}
                  testId={row.testId}
                  title={row.label}
                  amount={row.amount}
                  titleHref={row.href}
                />
              ))}
              {hasCreditGrants && (
                <>
                  <Totals.Divider />
                  <Totals.Row
                    title="Adjustment subtotal"
                    amount={settlement.adjustmentNetTotal}
                  />
                  <Totals.Row
                    title="Tax"
                    amount={settlement.adjustmentTotalTax}
                  />
                  {hasCreditNotes && <Totals.Divider />}
                </>
              )}
              {settlement.creditNoteRows.map(row => (
                <Totals.Row
                  key={row.key}
                  testId={row.testId}
                  title={row.label}
                  amount={row.amount}
                  titleHref={row.href}
                />
              ))}
              <Totals.Divider />
              <Totals.Row
                title="Adjustment total"
                amount={settlement.adjustmentGrossTotal}
              />
              <Totals.Divider />
            </>
          )}
          {showProjectedCredit && (
            <>
              <Totals.Row
                testId={EXPECTED_CREDIT_TEST_ID}
                title={EXPECTED_CREDIT_LABEL}
                amount={projectedCredit.times(-1).toString()}
                info={
                  <InfoPopover
                    variant="info"
                    minimal
                    padding="0"
                    placement="top"
                    body={<Box width={300}>{EXPECTED_CREDIT_INFO_BODY}</Box>}
                    data-testid={`${EXPECTED_CREDIT_TEST_ID}.info`}
                  />
                }
              />
              <Totals.Divider />
            </>
          )}
          {totalDueLine}
        </>
      )}
      {hasPayment && (
        <>
          <Totals.Divider />
          <Totals.Row title="Amount paid" amount={-amountPaid} />
          <Totals.Total
            title="Amount remaining"
            amount={amountRemaining}
            appendCurrencyCode
          />
        </>
      )}
    </>
  ) : (
    <Totals.Total amount={grossTotal} />
  )

  return (
    <Grid
      gridTemplateColumns={`266fr 60fr 76fr 44fr 144fr ${isEditable ? '32px' : '0px'}`}
    >
      <GridItem
        gridColumn={isEditable ? '2 / 6' : '2 / -1'}
        paddingLeft="4px"
        data-testid="invoice.lineItemTotals"
      >
        <Totals currency={currency}>
          <Totals.Row title="Subtotal" amount={netTotal} />
          {breakdownRows.taxRows.map(([title, amount]) => (
            <Totals.Row key={title} title={title} amount={amount} />
          ))}
          <Totals.Divider />
          {totalsBody}
        </Totals>
      </GridItem>
    </Grid>
  )
}
